import { FocusEvent, useState } from 'react';
import {
  CountrySelector,
  defaultCountries,
  FlagImage,
  usePhoneInput,
} from 'react-international-phone';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames';

import { useFormTheme } from '../../contexts/FormTheme/FormThemeContext';
import { TelephoneWithDialCodeViewModel } from '../../types/telephoneWithDialCode';

const TelephoneWithDialCode: React.FC<ValueFieldProps<TelephoneWithDialCodeViewModel>> = (
  props
) => {
  const { field, isValid, errors, tracker } = props;
  const { id, name } = field.valueField;
  const { countryCodeLabel, phoneNumberLabel, placeholderText, required } = field.model;
  const FieldValidationErrors = props.fieldValidationErrorsComponent;

  const [hasFocus, setHasFocus] = useState(false);
  const [hasCountryFocus, setHasCountryFocus] = useState(false);

  const { primaryTextColor } = useFormTheme();

  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'us',
    value: props.value || '',
    countries: defaultCountries,
    disableDialCodeAndPrefix: true,
    onChange: ({ phone }) => {
      props.onChange(name, phone, isValid, []);
    },
  });

  const hasAnyError = !!errors?.length;

  const onFocus = (event: FocusEvent<HTMLInputElement>) => {
    tracker.onFocusField(field, event?.target?.value);
    setHasFocus(true);
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    tracker.onBlurField(field, event?.target?.value);
    setHasFocus(false);
  };

  return (
    <>
      <div className="grid grid-cols-[2fr_3fr] gap-x-3">
        {(!!countryCodeLabel || !!phoneNumberLabel) && (
          <>
            <div>
              {!!countryCodeLabel && (
                <div className={classNames('mb-2 text-sm font-medium', primaryTextColor)}>
                  <label htmlFor={id}>{countryCodeLabel}</label>
                  {required && <span className=".text-error pl-0.5 font-medium">*</span>}
                </div>
              )}
            </div>
            <div>
              {!!phoneNumberLabel && (
                <div className={classNames('mb-2 text-sm font-medium', primaryTextColor)}>
                  <label htmlFor={id}>{phoneNumberLabel}</label>
                  {required && <span className=".text-error pl-0.5 font-medium">*</span>}
                </div>
              )}
            </div>
          </>
        )}

        <div className="min-w-0">
          <CountrySelector
            selectedCountry={country.iso2}
            onSelect={({ iso2 }) => setCountry(iso2)}
            buttonContentWrapperStyle={{ display: 'none' }}
            renderButtonWrapper={({ children, rootProps }) => (
              <button
                {...rootProps}
                type="button"
                onFocus={() => setHasCountryFocus(true)}
                onBlur={() => setHasCountryFocus(false)}
                className={classNames(
                  'tsp-country-selector flex h-[3.25rem] w-full items-center gap-2 rounded-lg border border-gray-100 bg-white px-3 py-3 outline-none',
                  hasCountryFocus && 'border-yellow',
                  hasAnyError && 'border-error bg-error/20'
                )}
              >
                <FlagImage iso2={country.iso2} size="24px" />
                <span className="tsp-country-code shrink-0 whitespace-nowrap text-base text-black">
                  +{country.dialCode}
                </span>
                <span className="tsp-country-name min-w-0 truncate text-sm text-black/60">
                  {country.name}
                </span>
                {children}
              </button>
            )}
          />
        </div>

        <div className="min-w-0">
          <input
            type="tel"
            id={id}
            name={name}
            ref={inputRef}
            value={inputValue}
            onChange={handlePhoneValueChange}
            placeholder={placeholderText}
            required={required}
            onFocus={onFocus}
            onBlur={onBlur}
            className={classNames(
              'h-[3.25rem] w-full rounded-lg border border-gray-100 bg-white px-4 py-3 text-lg text-black outline-none',
              hasFocus && 'border-yellow',
              hasAnyError && 'border-error bg-error/20'
            )}
          />
        </div>
      </div>

      {!!FieldValidationErrors && <FieldValidationErrors {...props} />}
    </>
  );
};

export default TelephoneWithDialCode;
